import {useEffect} from 'react';

import {ErrorMessage, Field, Form, FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Avatar, Box, Card, CardContent, Grid, Typography} from '@mui/material';

import {DepartmentFormValues} from './types/CompanyDepartmentFormTypes';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {EmpionSelectField} from '../../../../../components/EmpionSelectField/EmpionSelectField';
import {EmpionTextField} from '../../../../../components/EmpionTextField/EmpionTextField';
import {EmpionTooltip} from '../../../../../components/EmpionTooltip/EmpionTooltip';
import {useMethodTextProfileOptions} from '../../hooks/useMethodTextProfileOptions';
import {DEFAULT_METHOD_TEXT_PROFILE} from '../../types/MethodTextProfileTypes';

interface Options {
  label: string;
  id: string;
}

interface Props {
  header: string;
  options?: Options[];
  onClose(): void;
  selectLabel?: string;
  englishLabel?: string;
  germanLabel?: string;
  methodTextProfileLabel?: string;
  tooltip?: string;
  helperText?: string;
  onSubmit: (values: DepartmentFormValues) => void;
  open: boolean;
  initialValues?: DepartmentFormValues;
}

const defaultValues: DepartmentFormValues = {
  select: '',
  labelDe: '',
  labelEn: '',
  methodTextProfile: DEFAULT_METHOD_TEXT_PROFILE,
};

export const CompanyDepartmentModal = ({
  header,
  options,
  onClose,
  selectLabel,
  englishLabel,
  germanLabel,
  methodTextProfileLabel,
  tooltip,
  helperText,
  onSubmit,
  open,
  initialValues,
}: Props) => {
  const {t} = useTranslation();
  const {t: tModal} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal'});
  const {options: methodTextProfileOptions} = useMethodTextProfileOptions();

  const formik = useFormik({
    initialValues: initialValues ?? defaultValues,
    enableReinitialize: true,
    onSubmit,
    validate: (values) => {
      const errors: Partial<DepartmentFormValues> = {};

      if (!values.labelDe) {
        errors.labelDe = t('shared.required') || '';
      }

      if (!values.labelEn) {
        errors.labelEn = t('shared.required') || '';
      }

      if (options && !values.select) {
        errors.select = t('shared.required') || '';
      }

      return errors;
    },
  });

  useEffect(() => {
    if (open) {
      document.getElementById('departmentSubmitButton')?.focus();
    }
  }, [open]);

  const renderEndAdornment = (endAdornment: string) => {
    return (
      <Avatar sx={{bgcolor: 'transparent'}}>
        <span className={`fi fi-${endAdornment}`}></span>
      </Avatar>
    );
  };

  return (
    <FormikProvider value={formik}>
      <Form>
        <Typography variant='h3' gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {header}
          {!!tooltip && (
            <EmpionTooltip title={tooltip} isEnabled placement='right'>
              <InfoOutlinedIcon fontSize='small' />
            </EmpionTooltip>
          )}
        </Typography>

        <Grid container spacing={2}>
          {options && (
            <Grid item xs={12}>
              <Field
                type='select'
                component={EmpionSelectField}
                options={options}
                label={selectLabel}
                size='small'
                name='select'
                required
                helperText={helperText}
              />
              <Typography variant='body2' color='error' marginTop={1}>
                <ErrorMessage name='select' component='span' />
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Field
              type='text'
              component={EmpionTextField}
              label={germanLabel}
              size='small'
              name='labelDe'
              fullWidth
              endAdornment={renderEndAdornment('de')}
              required
            />
            <Typography variant='body2' color='error' marginTop={1}>
              <ErrorMessage name='labelDe' component='span' />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Field
              type='text'
              component={EmpionTextField}
              label={englishLabel}
              size='small'
              name='labelEn'
              fullWidth
              endAdornment={renderEndAdornment('gb')}
              required
            />
            <Typography variant='body2' color='error' marginTop={1}>
              <ErrorMessage name='labelEn' component='span' />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Card variant='outlined' sx={{borderRadius: 2}}>
              <CardContent>
                <Typography variant='subtitle2' gutterBottom>
                  {methodTextProfileLabel}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
                  {tModal('methodTextProfileDescription')}
                </Typography>
                <Field
                  type='select'
                  component={EmpionSelectField}
                  options={methodTextProfileOptions}
                  size='small'
                  name='methodTextProfile'
                  required
                />
                <Typography variant='body2' color='error' marginTop={1}>
                  <ErrorMessage name='methodTextProfile' component='span' />
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box display='flex' justifyContent='flex-end' gap={2} marginTop={4}>
          <EmpionButton type='button' variant='secondary' onClick={onClose}>
            {t('shared.formButtons.cancel')}
          </EmpionButton>
          <EmpionButton type='submit' variant='primary' id='departmentSubmitButton'>
            {t('shared.formButtons.save')}
          </EmpionButton>
        </Box>
      </Form>
    </FormikProvider>
  );
};
